/* ---------------------------------------------------------------------------
 * MD Fashionwear — cart sync + money formatting
 *
 * ADDING DOES NOT REBUILD THE DRAWER. Dispatching cart:refresh made the theme
 * rebuild the whole drawer about 2.8s later, throwing away the recommendation
 * carousel and every scroll position. After an add this fetches just the cart
 * drawer section through the Section Rendering API and swaps ONLY the line items,
 * the shipping bar and the totals.
 *
 * MONEY FORMAT: one shape everywhere, amount then ISO code, "200.00 USD".
 * Liquid's money filter varies by currency ("Lek 3,340.22 ALL" in Albania,
 * "AMD 27,818.51" in Armenia), so it is not used for these rows.
 *
 * THREE BUGS THAT WERE HERE. DO NOT REINTRODUCE ANY OF THEM.
 *
 * 1. Rates were cached against the COUNTRY ALONE, so the first lookup — made
 *    while the cart was below the free-shipping threshold — was reused forever. A
 *    Lebanese cart that grew to exactly 200 still displayed "Shipping 5.00 USD"
 *    while Shopify was correctly offering free shipping. The key now includes the
 *    cart signature, because rates depend on cart contents.
 *
 * 2. A MutationObserver watched the document for added nodes and repainted.
 *    Writing textContent CREATES a text node, so every repaint retriggered the
 *    observer, which fetched and repainted again — an endless loop that froze the
 *    tab. There is no DOM observer here. Repaints come from cart events and from a
 *    poll that only reads an attribute.
 *
 * 3. data-painted was set BEFORE the shipping lookup resolved. Shopify returns
 *    nothing for a beat after a cart change, and that one empty result marked the
 *    block done, so "Calculated at checkout" stuck permanently. The marker is now
 *    only written once shipping is resolved, or after a few failed attempts so a
 *    market that genuinely cannot be quoted does not retry forever.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var rateCache = {};
  var attempts = {};
  var MAX_ATTEMPTS = 4;
  var TTL = 2 * 60 * 1000;
  var busy = false;

  function sectionId() {
    var el = document.querySelector('[id^="shopify-section-"][id*="cart-drawer"]');
    return el ? el.id.replace('shopify-section-', '') : null;
  }

  function code() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  }

  function fmt(cents) {
    var amount = (Number(cents) || 0) / 100;
    var n;
    try {
      n = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      n = amount.toFixed(2);
    }
    var c = code();
    return c ? n + ' ' + c : n;
  }

  function signature(country, cart) {
    return [country, code(), cart.token || '', cart.item_count,
            cart.items_subtotal_price, cart.total_price].join('|');
  }

  function fetchRates(country) {
    return fetch('/cart/shipping_rates.json?shipping_address%5Bcountry%5D=' + encodeURIComponent(country))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return (j && j.shipping_rates) || []; })
      .catch(function () { return []; });
  }

  function rates(country, cart) {
    if (!country) return Promise.resolve([]);

    var key = signature(country, cart);
    var hit = rateCache[key];
    if (hit && (Date.now() - hit.t) < TTL) return Promise.resolve(hit.list);

    return fetchRates(country).then(function (list) {
      if (list.length) rateCache[key] = { t: Date.now(), list: list };
      return list;
    });
  }

  function paintTotals() {
    var block = document.querySelector('.md-totals');
    if (!block || busy) return Promise.resolve();

    var subEl = document.getElementById('md-subtotal');
    var shipEl = document.getElementById('md-ship-amount');
    var totalEl = document.getElementById('md-grand-total');
    var country = block.getAttribute('data-country');

    busy = true;

    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      if (!cart) return;

      var sig = signature(country, cart);

      if (subEl) subEl.textContent = fmt(cart.items_subtotal_price);
      if (totalEl) totalEl.textContent = fmt(cart.total_price);

      if (!cart.item_count) {
        block.setAttribute('data-painted', sig);
        return;
      }

      return rates(country, cart).then(function (list) {
        attempts[sig] = (attempts[sig] || 0) + 1;

        if (!list.length) {
          // Leave the block unmarked so the poll tries again shortly, unless we
          // have already tried enough that this market clearly cannot be quoted
          // from a country name alone.
          if (attempts[sig] >= MAX_ATTEMPTS) block.setAttribute('data-painted', sig);
          return;
        }

        var cheapest = list.reduce(function (a, b) {
          return parseFloat(b.price) < parseFloat(a.price) ? b : a;
        });
        var ship = Math.round(parseFloat(cheapest.price) * 100);

        if (shipEl) {
          if (ship === 0) {
            shipEl.textContent = 'Free';
            shipEl.classList.remove('md-totals__value--muted');
            shipEl.classList.add('md-totals__value--free');
          } else {
            shipEl.textContent = fmt(ship);
            shipEl.classList.remove('md-totals__value--muted');
            shipEl.classList.remove('md-totals__value--free');
          }
        }

        if (totalEl) totalEl.textContent = fmt(cart.total_price + ship);
        block.setAttribute('data-painted', sig);
      });
    }).catch(function () {}).then(function () { busy = false; });
  }

  function swap(selector, doc) {
    var incoming = doc.querySelector(selector);
    var current = document.querySelector(selector);
    if (!incoming || !current || !current.parentNode) return;
    current.parentNode.replaceChild(document.importNode(incoming, true), current);
  }

  function afterAdd(item) {
    if (item) {
      document.dispatchEvent(new CustomEvent('variant:add', { detail: { items: [item] } }));
    }

    var id = sectionId();
    if (!id) return paintTotals();

    return fetch('/?sections=' + encodeURIComponent(id))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var html = j && j[id];
        if (!html) return;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        swap('.cart-drawer__items', doc);
        swap('.md-ship-bar', doc);
        swap('.md-totals', doc);
      })
      .catch(function () {})
      .then(function () {
        if (window.MDCartBar) window.MDCartBar.refresh();
        return paintTotals();
      });
  }

  function boot() {
    paintTotals();

    var force = function () { paintTotals(); };
    document.documentElement.addEventListener('cart:refresh', force);
    document.documentElement.addEventListener('cart:change', force);
    document.addEventListener('cart:change', force);
    document.addEventListener('variant:add', force);

    // Cheap attribute read. Unmarked means either fresh server markup or a
    // shipping lookup that has not resolved yet.
    setInterval(function () {
      var block = document.querySelector('.md-totals');
      if (block && !block.hasAttribute('data-painted')) paintTotals();
    }, 900);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.MDCartSync = { afterAdd: afterAdd, paintTotals: paintTotals, fmt: fmt };
})();
